import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function DirectorySubmit() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    kind: "church", name: "", description: "", city: "", state: "",
    address: "", website: "", phone: "", email: "", service_times: "", tags: "",
  });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.tags = payload.tags || [];
      const { data } = await api.post("/directory", payload);
      toast.success(data.status === "approved" ? "Listing approved instantly" : "Submitted — admin will review shortly");
      navigate(`/directory/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-8 py-10">
      <Link to="/directory" className="inline-flex items-center gap-1.5 text-sm text-jjblue/80 hover:text-burgundy mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to directory
      </Link>
      <div className="overline text-burgundy flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5" /> New Kingdom listing
      </div>
      <h1 className="font-serif text-4xl text-jjblue mt-2 leading-tight">Submit a place worth knowing.</h1>
      <p className="mt-2 text-[var(--jj-text-secondary)]">
        Tell us about a church, business, or ministry. Members can see it once an admin approves.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-3" data-testid="dir-submit-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--jj-muted)]">Type</label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {[
              { v: "church", l: "Church" },
              { v: "business", l: "Business" },
              { v: "ministry", l: "Ministry" },
            ].map((k) => (
              <button
                type="button"
                key={k.v}
                onClick={() => setForm((f) => ({ ...f, kind: k.v }))}
                className={`py-2 rounded-full border text-sm transition-colors ${
                  form.kind === k.v
                    ? "bg-burgundy text-cream border-burgundy"
                    : "bg-cream-surface text-jjblue border-jj hover:border-gold"
                }`}
                data-testid={`dir-kind-pick-${k.v}`}
              >
                {k.l}
              </button>
            ))}
          </div>
        </div>

        <input className="input-jj" placeholder="Name *" value={form.name} onChange={update("name")} data-testid="dir-name" />
        <textarea className="input-jj" rows={4} placeholder="What makes this place worth knowing?" value={form.description} onChange={update("description")} data-testid="dir-description" />
        <div className="grid grid-cols-2 gap-3">
          <input className="input-jj" placeholder="City" value={form.city} onChange={update("city")} data-testid="dir-city" />
          <input className="input-jj" placeholder="State / Region" value={form.state} onChange={update("state")} data-testid="dir-state" />
        </div>
        <input className="input-jj" placeholder="Street address (optional)" value={form.address} onChange={update("address")} />
        <div className="grid grid-cols-2 gap-3">
          <input className="input-jj" placeholder="Website (https://...)" value={form.website} onChange={update("website")} />
          <input className="input-jj" placeholder="Phone" value={form.phone} onChange={update("phone")} />
        </div>
        <input className="input-jj" placeholder="Public contact email" value={form.email} onChange={update("email")} />
        {form.kind === "church" && (
          <input className="input-jj" placeholder="Service times (e.g. Sun 9am, 11am · Wed 7pm)" value={form.service_times} onChange={update("service_times")} data-testid="dir-service-times" />
        )}
        <input className="input-jj" placeholder="Tags (comma-separated)" value={form.tags} onChange={update("tags")} />

        <div className="flex items-center justify-end gap-3 pt-4">
          <Link to="/directory" className="btn-secondary !py-2 !px-4 text-sm">Cancel</Link>
          <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="dir-submit-confirm">
            {busy ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </form>
    </div>
  );
}
